import { useState } from 'react';

import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { match } from 'ts-pattern';

import { Button } from '@nesso/ui/primitives/button';

import { PermissionGate } from '@/entities/practice-member';
import { notesSettingsOptions } from '@/entities/practice-member-settings';
import type { Session } from '@/entities/session';
import { toNoteSettings, useGenerateNoteOptions } from '@/entities/session-note';
import { EditTreatmentApproaches } from '@/features/session-note/edit-treatment-approaches';
import { TooltipButton } from '@/shared/ui/common/tooltip-button';

type Props = {
    session: Session;
};

export function GenerateSessionNote({ session }: Props) {
    const [openTreatmentApproaches, setOpenTreatmentApproaches] = useState(false);
    const { isPending, mutate } = useMutation(useGenerateNoteOptions(session.id));
    const { data: notesSettings } = useSuspenseQuery(notesSettingsOptions());

    const type = session.currentNote?.type ?? notesSettings.value.defaultNoteTemplate;
    const settings =
        session.currentNote?.generator?.settings ?? toNoteSettings(notesSettings.value);

    const generateNote = () => {
        if (!session.treatmentApproaches.length) {
            setOpenTreatmentApproaches(true);
        } else {
            mutate({
                sessionId: session.id,
                settings,
                type,
            });
        }
    };

    return (
        <div className="flex flex-col items-center justify-center h-full space-y-4">
            {!session.treatmentApproaches.length && (
                <EditTreatmentApproaches
                    sessionId={session.id}
                    selectedApproaches={session.treatmentApproaches}
                    type={type}
                    settings={settings}
                    popoverContentProps={{
                        side: 'bottom',
                        align: 'center',
                        className: 'min-w-[375px] p-6 gap-4',
                    }}
                    buttonClassName="invisible w-0 h-0 overflow-hidden"
                    onOpenChange={setOpenTreatmentApproaches}
                    open={openTreatmentApproaches}
                />
            )}
            <p className="text-sm text-muted-foreground">
                The note for this session has been deleted
            </p>
            <PermissionGate
                requiredAccessSources={['CLINICIAN', 'NOTES_BASE', 'NOTES_ACTIVE']}
                fallback={(missing) => (
                    <TooltipButton
                        variant="default"
                        size="sm"
                        disabled
                        tooltip={match(missing)
                            .when(
                                (m) => m.includes('NOTES_BASE'),
                                () => 'Add Notes to your plan to generate session notes',
                            )
                            .when(
                                (m) => m.includes('NOTES_ACTIVE'),
                                () => 'Update billing information to manage your session notes',
                            )
                            .otherwise(() => 'This is currently unavailable')}
                    >
                        Generate Note
                    </TooltipButton>
                )}
            >
                <Button onClick={generateNote} variant="default" size="sm" disabled={isPending}>
                    {isPending ? 'Generating...' : 'Generate Note'}
                </Button>
            </PermissionGate>
        </div>
    );
}
